import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';

import { useI18n } from '../../../../../../application/language/useI18n';
import { IUnitCardModel } from '../../../../../../service/unit/interface/IUnitCardModel';
import { IUnitResultModel } from '../../../../../../service/unitResult/IUnitResultModel';
import { getLocaleByCount } from '../../../../../../utils/localeByCount';
import { PracticeIcons } from '../../icons/PracticeIcon';
import { TimeIcon } from '../../icons/TimeIcon';
import { UnitIcon } from '../../icons/UnitIcon';
import { UNIT_LIST_ITEMS_STYLES, getUnitStatus } from '../utils/passingListUserCard';
import { useFormatTime } from '../utils/useFormatTime';
import { Colors } from '../../../../../design/color/Colors';

type Props = {
  unit: IUnitCardModel;
  unitResult?: IUnitResultModel;
};

const useStyles = makeStyles(() => ({
  row: { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', width: '100%', marginBottom: '16px' },
  wrapGap2: { display: 'flex', flexWrap: 'wrap', gap: '16px' },
  item: { display: 'flex', gap: '4px', alignItems: 'center' },
  score: { display: 'flex', alignItems: 'center', color: Colors.textTertiary },
  scoreMargin: { marginRight: '4px' },
}));

export const UnitCardStats = observer(({ unit, unitResult }: Props) => {
  const { i18n } = useI18n();
  const classes = useStyles();
  const unitStatus = getUnitStatus(unitResult?.isPassed);

  const questionsWords = useMemo(
    () => i18n.language === 'en' ? ['question', 'questions', 'questions'] : ['вопрос', 'вопроса', 'вопросов'],
    [i18n.language],
  );
  const tasksWords = useMemo(
    () => i18n.language === 'en' ? ['task', 'tasks', 'tasks'] : ['практика', 'практики', 'практик'],
    [i18n.language],
  );

  const totalTime =
    Number(unit?.quiz.isEnabled && unit?.quiz.passageTime) +
    Number(unit?.practice.isEnabled && unit?.practice.passageTime) +
    (unit?.theory.isEnabled ? 30 : 0);

  return (
    <Box className={classes.row}>
      <Box className={classes.wrapGap2}>
        {unit?.quiz.isEnabled && (
          <Box className={classes.item}>
            <UnitIcon />
            <Box>{getLocaleByCount(unit?.quiz.questionsCount, questionsWords)}</Box>
          </Box>
        )}
        {unit?.practice.isEnabled && (
          <Box className={classes.item}>
            <PracticeIcons />
            <Box>{getLocaleByCount(unit?.practice.tasksCount, tasksWords)}</Box>
          </Box>
        )}
        <Box className={classes.item}>
          <TimeIcon />
          <Box>{useFormatTime(totalTime)}</Box>
        </Box>
      </Box>

      <Box className={classes.score}>
        <Box className={classes.scoreMargin}>
          <span style={{ color: UNIT_LIST_ITEMS_STYLES[unitStatus].color }}>
            {Math.round(unitResult?.totalUnitResult || 0)}
          </span>
          /{Math.round(unit.score)}
        </Box>
        {UNIT_LIST_ITEMS_STYLES[unitStatus].icon}
      </Box>
    </Box>
  );
});
